import ReactDOM from 'react-dom/client';
import { Provider } from 'react-redux';
import { MemoryRouter as Router } from 'react-router-dom';
import './index.css';
import './i18n';
import APIGateway from 'api/gateway';
import { store } from 'app/store';
import { updateTokenAfterReauth } from 'slices/auth';
import { installBackTrap } from 'utils/backTrap';
import { getLinearAccelerationMagnitude } from 'utils/platform';
import { armBackForwardGuard } from 'utils/returnGuard';
import { wipeOutTraces } from 'utils/storage';
import {
  collectExternalOrigins,
  requestExternalOriginWipes,
  signalIframesCleanup,
} from 'utils/teardown';
import Shell from './Shell';
import * as serviceWorkerRegistration from './serviceWorkerRegistration';

// FingerprintJS (~118 kB) is submit-time only — load it lazily so it stays out of the pre-auth
// Shell's first-paint chunk. Deliberately NOT warmed here: the initial login runs in the auth
// iframe (which warms its own FingerprintJS), so on the main domain the engine is only needed at a
// later re-auth. FingerprintJS.load() eagerly runs its entropy sources (~hundreds of ms); warming
// it during the initial load only duplicates that cost inside the TBT window. The first real call
// (re-auth) lazy-loads it instead.
let fpPromise;
const loadFingerprint = () => {
  if (!fpPromise) {
    fpPromise = import('@fingerprintjs/fingerprintjs').then((m) => m.default.load());
  }
  return fpPromise;
};

// Lazy load Sentry — only when a DSN is configured. Without the early return the
// dynamic import runs unconditionally and pulls Sentry's whole module tree, even on
// dev/no-DSN builds where Sentry never inits.
(async () => {
  if (!import.meta.env.VITE_SENTRY_DSN) return;
  const { init } = await import('@sentry/react');
  init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    integrations: [],
  });
})();

// if (window.self !== window.top && window.parent.location.host !== import.meta.env.VITE_DOMAIN) { // TODO: use postMessage to check
//   document.body.style.display = 'none !important';
//   throw Error('Go fuck yourself with trying to iFrame me!');
// } // FIXME

// Lazy Firebase initialization
let firebaseInitPromise = null;
let firebaseMessaging = null;

const initializeFirebase = async (_registration) => {
  if (firebaseInitPromise) {
    return firebaseInitPromise;
  }

  firebaseInitPromise = (async () => {
    try {
      const [{ initializeApp }, { getMessaging, getToken, isSupported }] = await Promise.all([
        import('firebase/app'),
        import('firebase/messaging'),
      ]);

      const firebaseApp = initializeApp({
        apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
        authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
        projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
        storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
        messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
        appId: import.meta.env.VITE_FIREBASE_APP_ID,
      });

      const supported = await isSupported();
      firebaseMessaging = supported ? getMessaging(firebaseApp) : null;

      return { getToken };
    } catch (e) {
      console.error('Firebase initialization error:', e);
      return null;
    }
  })();

  return firebaseInitPromise;
};

window.App = {
  installable: false,
  installed: false,
  deferredInstallPrompt: null,
  getFirebaseToken: async () => {
    const registration = await navigator.serviceWorker?.ready;
    if (!registration) return null;

    const firebase = await initializeFirebase(registration);
    if (!firebase || !firebaseMessaging) return null;

    return firebase.getToken(firebaseMessaging, {
      vapidKey: import.meta.env.VITE_FIREBASE_VAPID_KEY,
      serviceWorkerRegistration: registration,
    });
  },
  getFingerprint: async () => {
    const fp = await loadFingerprint();
    const result = await fp.get();
    return result;
  },
  // Best-effort push revocation for the deep signout wipe: invalidates the FCM token (when
  // messaging was initialized this session) and kills the browser-side subscription.
  revokePushSubscription: async () => {
    try {
      if (firebaseMessaging) {
        const { deleteToken } = await import('firebase/messaging');
        await deleteToken(firebaseMessaging);
      }
    } catch (_err) {
      // Ignore errors
    }
    try {
      const registration = await navigator.serviceWorker?.getRegistration?.();
      const subscription = await registration?.pushManager?.getSubscription();
      await subscription?.unsubscribe();
    } catch (_err) {
      // Ignore errors
    }
  },
};

serviceWorkerRegistration.register({
  onRegister: async (_registration) => {
    // Firebase will be initialized lazily when getFirebaseToken is called
  },
  onUpdate: async (registration) => {
    console.log('sw updated', registration);
  },
});

if (import.meta.env.DEV) {
  window.APIGateway = APIGateway;
}

// Closing the tab is an exit too, and the shortest one there is: no async step survives it. Both
// steps are therefore fire-and-forget — the in-page wipe of each embedded app, and a request to
// their origins that outlives this tab.
//
// Bound to pagehide as well, because mobile browsers routinely discard a tab without ever firing
// beforeunload. Idempotent, so firing on both costs nothing.
const signalEmbeddedAppWipes = () => {
  signalIframesCleanup();
  requestExternalOriginWipes(collectExternalOrigins());
};

// Our own sync wipe stays on beforeunload ALONE. The forward-return guard writes its leave marker
// to sessionStorage on pagehide precisely because that runs after this wipe (see utils/returnGuard);
// wiping on pagehide too would race that write and erase the marker, letting a Back traversal walk
// straight back into the app.
window.onbeforeunload = (_e) => {
  signalEmbeddedAppWipes();
  wipeOutTraces();
};
window.addEventListener('beforeunload', window.onbeforeunload, false);
window.addEventListener('pagehide', signalEmbeddedAppWipes);

window.addEventListener('devicemotion', (event) => {
  const adjusted = getLinearAccelerationMagnitude(event);
  if (adjusted != null && adjusted > 0.1) {
    document.dispatchEvent(new CustomEvent('deviceacceleration'));
  }
});

window.addEventListener('beforeinstallprompt', (e) => {
  e.preventDefault();
  window.App.deferredInstallPrompt = e;
  window.App.installable = true;
});

window.addEventListener('appinstalled', (_evt) => {
  window.App.installed = true;
});

// Parse initial route from hash (for #/join/... sharing links) before clearing it
const initialRoute = window.location.hash ? window.location.hash.replace(/^#/, '') : '/';
// Clear hash from URL to leave no trace in browser history
if (window.location.hash) {
  window.history.replaceState(null, '', window.location.pathname + window.location.search);
}

// Browser Back-button trap (see utils/backTrap). Keeps a load-filled jail of history
// sentinels so the browser Back button reliably routes into React (useBrowserBack) for in-
// app navigation, including several presses in a row.
installBackTrap();

// Forward-return guard (see utils/returnGuard + the head-script in index.html): once the
// app IS left (user backs out past the login page, or the jail is exhausted), it can never
// be re-reached via Back/Forward — it bounces to a neutral page instead.
armBackForwardGuard();

// Hosted by a native browser that owns the session (window.In6to.hosted, set before this bundle runs
// and unreachable by ordinary web content): take the session it handed us straight into the store, so
// the app comes up logged in without the login iframe or a postMessage handshake. The browser is the
// trusted environment, so the pre-auth safety gate and the login surface are both skipped for it (see
// Shell) and the login iframe is never created (see index.html). The server still enforces the
// token's own lifetime and re-auth, so a forged marker cannot outlive server policy.
if (window.In6to?.hosted) {
  let appliedToken = null;
  const applyHostedSession = (s) => {
    if (!s?.token || s.token === appliedToken) return; // no session, or the same one already applied
    appliedToken = s.token;
    store.dispatch(
      updateTokenAfterReauth({
        token: s.token,
        user: s.user || { jwt: s.token },
        encryptionKey: s.encryptionKey,
        navigationState: s.navigationState ?? null,
      })
    );
  };
  // The host may hand the session in either order: a value set before this bundle ran, or a later
  // push (also used to refresh the token when the host re-confirms identity). Both funnel here.
  applyHostedSession(window.In6to.session);
  window.In6to.setSession = (raw) => {
    try {
      applyHostedSession(typeof raw === 'string' ? JSON.parse(raw) : raw);
    } catch {
      /* ignore a malformed push */
    }
  };
}

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <Router initialEntries={[initialRoute]}>
    <Provider store={store}>
      <Shell />
    </Provider>
  </Router>
);
